/**
 * Cities: site quality, capital placement, per-faction city placement and fair sizing.
 *
 * - Site score favours plains, coasts (ports), river banks and river mouths; mountains and marsh
 *   are never settled.
 * - Capitals: farthest-point sampling over land (geodesic BFS distance) seeds N points, a few Lloyd
 *   iterations move each to the middle of its share of the continent, then the capital takes the
 *   best site nearby. Result: capitals far apart, each in its own heartland.
 * - Other cities are placed *after* territories are drawn, with an equal quota per faction, taking
 *   turns so no faction grabs all the good border sites first. Spacing ≥ MIN_SPACING hexes.
 * - Sizes are dealt out greedily to whichever faction currently has the fewest victory points, so
 *   VP / industry / manpower end up balanced while big cities still sit on each faction's best sites.
 */
import { CAPITAL_VP_BONUS, CITY_YIELDS } from '../core/constants';
import type { Rng } from '../core/rng';
import { isWater, NO_FACTION, Terrain } from '../core/types';
import { isLand, landHexes, type MapContext } from './context';
import { bfs } from './paths';
import type { RiverResult } from './rivers';

/** Minimum hex distance between two cities. */
export const MIN_SPACING = 3;
const UNSUITABLE = -Infinity;

/** A placed city before naming: hex, owner and size. */
export interface CitySite {
  hex: number;
  owner: number;
  capital: boolean;
  size: 1 | 2 | 3;
}

/**
 * Target city count: 16 / 26 / 40 on small / medium / large boards (1632 / 2816 / 4704 hexes),
 * linear in between, a little more with more factions and never fewer than 4 per faction.
 */
export function cityTarget(hexCount: number, factionCount: number): number {
  const base = hexCount <= 2816 ? 16 + ((hexCount - 1632) * 10) / 1184 : 26 + ((hexCount - 2816) * 14) / 1888;
  return Math.max(factionCount * 4, Math.round(base * (1 + 0.06 * (factionCount - 3))));
}

export function siteScores(ctx: MapContext, rivers: RiverResult): Float64Array {
  const { grid, n, terrain } = ctx;
  const score = new Float64Array(n).fill(UNSUITABLE);
  for (let h = 0; h < n; h++) {
    const t = terrain[h];
    let s: number;
    if (t === Terrain.Plains) s = 1;
    else if (t === Terrain.Hills) s = 0.45;
    else if (t === Terrain.Forest) s = 0.35;
    else continue;
    let sea = 0;
    let lake = 0;
    let flat = 0;
    let rugged = 0;
    grid.forNeighbours(h, (m) => {
      const u = terrain[m];
      if (u === Terrain.Ocean || u === Terrain.Shallows) sea = 1;
      else if (u === Terrain.Lake) lake = 1;
      else if (u === Terrain.Plains) flat++;
      else if (u === Terrain.Mountains) rugged = 1;
    });
    s += 0.35 * sea + 0.3 * lake + 0.2 * (flat / 6) - 0.25 * rugged;
    if (rivers.bankWidth[h] > 0) s += 0.3 + 0.08 * rivers.bankWidth[h];
    if (rivers.mouth[h]) s += 0.3;
    score[h] = s;
  }
  return score;
}

export function placeCapitals(ctx: MapContext, rng: Rng, score: Float64Array, count: number): number[] {
  const { grid, n, ux, uy } = ctx;
  const land = landHexes(ctx);
  const landMask = new Uint8Array(n);
  for (const h of land) landMask[h] = 1;
  let suitable = land.filter((h) => score[h] >= 0.9);
  if (suitable.length < count) suitable = land.filter((h) => score[h] > UNSUITABLE);

  // Farthest-point sampling: each new seed is the suitable hex farthest (over land) from all seeds.
  const seeds = [rng.pick(suitable)];
  while (seeds.length < count) {
    const { dist } = bfs(grid, seeds, landMask);
    let best = suitable[0];
    for (const h of suitable) if (dist[h] > dist[best]) best = h;
    seeds.push(best);
  }

  // Lloyd relaxation: move every seed to the land hex nearest its cell's centroid.
  for (let iter = 0; iter < 6; iter++) {
    const { label } = bfs(grid, seeds, landMask);
    const sx = new Float64Array(count);
    const sy = new Float64Array(count);
    const sn = new Float64Array(count);
    for (const h of land) {
      const l = label[h];
      sx[l] += ux[h];
      sy[l] += uy[h];
      sn[l]++;
    }
    const bestD = new Float64Array(count).fill(Infinity);
    const next = seeds.slice();
    for (const h of land) {
      const l = label[h];
      const dx = ux[h] - sx[l] / sn[l];
      const dy = uy[h] - sy[l] / sn[l];
      const d = dx * dx + dy * dy;
      if (d < bestD[l]) {
        bestD[l] = d;
        next[l] = h;
      }
    }
    for (let i = 0; i < count; i++) seeds[i] = next[i];
  }

  // Final capital: the best site within 2 hexes of the relaxed seed, inside the seed's cell.
  const { label } = bfs(grid, seeds, landMask);
  return seeds.map((seed, i) => {
    let best = seed;
    let bestScore = UNSUITABLE;
    for (const h of grid.range(seed, 2)) {
      if (label[h] !== i || score[h] === UNSUITABLE) continue;
      const s = score[h] + rng.range(0, 0.3);
      if (s > bestScore) {
        bestScore = s;
        best = h;
      }
    }
    return best;
  });
}

/**
 * Moves every capital to the best site within 2 hexes of its territory's centroid (inside the
 * territory). One step of a "balanced Lloyd" iteration: capitals boxed into a corner of the
 * continent move towards the middle of their land, so the regrown territories balance better.
 */
export function recentreCapitals(
  ctx: MapContext,
  rng: Rng,
  score: Float64Array,
  owner: Uint8Array,
  capitals: readonly number[],
): number[] {
  const { grid, n, ux, uy } = ctx;
  return capitals.map((capital, f) => {
    let sx = 0;
    let sy = 0;
    let k = 0;
    for (let h = 0; h < n; h++) {
      if (owner[h] !== f) continue;
      sx += ux[h];
      sy += uy[h];
      k++;
    }
    let centre = capital;
    let bestD = Infinity;
    for (let h = 0; h < n; h++) {
      if (owner[h] !== f) continue;
      const d = (ux[h] - sx / k) * (ux[h] - sx / k) + (uy[h] - sy / k) * (uy[h] - sy / k);
      if (d < bestD) {
        bestD = d;
        centre = h;
      }
    }
    let best = capital;
    let bestScore = UNSUITABLE;
    for (const h of grid.range(centre, 2)) {
      if (owner[h] !== f || score[h] === UNSUITABLE) continue;
      const s = score[h] + rng.range(0, 0.3);
      if (s > bestScore) {
        bestScore = s;
        best = h;
      }
    }
    return best;
  });
}

/**
 * Places the non-capital cities: equal quota per faction (remainder to the largest territories),
 * factions take turns picking their best free site. A strong bonus for distance from existing
 * cities spreads them over the whole territory instead of lining every coast and river.
 */
export function placeCities(
  ctx: MapContext,
  rng: Rng,
  score: Float64Array,
  owner: Uint8Array,
  capitals: number[],
  total: number,
): CitySite[] {
  const { grid, n } = ctx;
  const factions = capitals.length;
  const territory: number[][] = Array.from({ length: factions }, () => []);
  for (let h = 0; h < n; h++)
    if (owner[h] !== NO_FACTION && score[h] > UNSUITABLE) territory[owner[h]].push(h);

  const extra = Math.max(0, total - factions);
  const quota = new Array<number>(factions).fill(Math.floor(extra / factions));
  const bySize = [...territory.keys()].sort((a, b) => territory[b].length - territory[a].length || a - b);
  for (let i = 0; i < extra % factions; i++) quota[bySize[i]]++;

  const jitter = new Float64Array(n);
  for (let h = 0; h < n; h++) jitter[h] = rng.range(0, 0.3);
  const nearest = new Int32Array(n).fill(99);
  const sites: CitySite[] = [];
  const add = (hex: number, f: number, capital: boolean) => {
    sites.push({ hex, owner: f, capital, size: capital ? 3 : 1 });
    for (const h of grid.range(hex, 10)) nearest[h] = Math.min(nearest[h], grid.distance(h, hex));
  };
  for (let f = 0; f < factions; f++) add(capitals[f], f, true);

  const order = rng.shuffle([...territory.keys()]);
  for (let progress = true; progress; ) {
    progress = false;
    for (const f of order) {
      if (quota[f] <= 0) continue;
      let best = -1;
      let bestValue = -Infinity;
      for (const h of territory[f]) {
        if (nearest[h] < MIN_SPACING) continue;
        const v = 0.6 * score[h] + 0.16 * Math.min(nearest[h], 10) + jitter[h];
        if (v > bestValue) {
          bestValue = v;
          best = h;
        }
      }
      if (best < 0) {
        quota[f] = 0;
        continue;
      }
      add(best, f, false);
      quota[f]--;
      progress = true;
    }
  }
  assignSizes(sites, score, factions, rng);
  return sites;
}

/**
 * Upgrades towns to cities (size 2) and metropolises (size 3). Each upgrade goes to the faction
 * that currently has the fewest VP, on its best remaining site — the balancing lever for fairness.
 */
function assignSizes(sites: CitySite[], score: Float64Array, factions: number, rng: Rng): void {
  const vp = new Array<number>(factions).fill(0);
  const towns: CitySite[][] = Array.from({ length: factions }, () => []);
  for (const s of sites) {
    vp[s.owner] += CITY_YIELDS[s.size].vp + (s.capital ? CAPITAL_VP_BONUS : 0);
    if (!s.capital) towns[s.owner].push(s);
  }
  for (const list of towns) list.sort((a, b) => score[b.hex] - score[a.hex] || a.hex - b.hex);
  const nonCapitals = sites.length - factions;
  const tieBreak = rng.shuffle([...vp.keys()]);
  const upgrade = (size: 2 | 3) => {
    let pick = -1;
    for (const f of tieBreak) if (towns[f].length > 0 && (pick < 0 || vp[f] < vp[pick])) pick = f;
    if (pick < 0) return;
    const site = towns[pick].shift()!;
    vp[pick] += CITY_YIELDS[size].vp - CITY_YIELDS[site.size].vp;
    site.size = size;
  };
  // Design mix: ~7 % of non-capitals become metropolises, ~30 % cities. A metropolis (+5 VP) is worth
  // 2.5 city upgrades (+2 each); keep only as many metropolises as the city upgrades can balance
  // out across the other factions, and trade the rest for city upgrades.
  const wanted = Math.round(nonCapitals * 0.07);
  let metropolises = wanted;
  let cities = Math.round(nonCapitals * 0.3);
  for (; metropolises > 0; metropolises--) {
    const spare = metropolises % factions;
    const budget = cities + Math.round(2.5 * (wanted - metropolises));
    if (spare === 0 || budget >= 2.5 * (factions - spare)) break;
  }
  cities += Math.round(2.5 * (wanted - metropolises));
  for (let i = 0; i < metropolises; i++) upgrade(3);
  for (let i = 0; i < cities; i++) upgrade(2);
}

/** Adjacent to sea or lake water. */
export function isCoastal(ctx: MapContext, h: number): boolean {
  let coastal = false;
  ctx.grid.forNeighbours(h, (m) => {
    if (isWater(ctx.terrain[m])) coastal = true;
  });
  return coastal && isLand(ctx.terrain[h]);
}
